import type { ThunkAction, ThunkDispatch } from 'redux-thunk';

/* Shared type module for the Product Costing slice. Mirrors the sibling CostingTsx/MyTodo/Samples
   convention: the action-type map, domain shapes, slice state, the action union and the thunk
   aliases all live here, because the reducer, the thunks, the context provider and every table
   need the same shapes.

   This is a strict port of NewProductCosting/store/*. The action-type STRING VALUES are
   byte-identical to the originals — other slices and any in-flight persisted state key off them. */

/**
 * Response envelope returned by the services/ layer. The axios interceptor resolves
 * response.data, so callers receive this object rather than an AxiosResponse.
 */
export interface ApiResponse<T = unknown> {
  code?: number;
  message?: string;
  data?: T;
}

/** The `{ errMsg }` body the API returns on a 400/500. */
export interface ApiErrorData {
  errMsg?: string;
}

/** A `{ _id, name }` reference as embedded by the API in populated fields. */
export interface NamedRef {
  _id?: string;
  name?: string;
}

/** Translator passed down from callers as `t`. Named `transactionMsg` in the original thunks. */
export type TransactionMsg = (key: string) => string;

/* ------------------------------------------------------------------ action types */

const ProductCostingType = {
  COSTING_GET_TESTING_BOM_LIST: 'COSTING_GET_TESTING_BOM_LIST',
  COSTING_MAEK_AS_COMPLETE: 'COSTING_MAEK_AS_COMPLETE',
  COSTING_ADD_PRODUCT_COSTING_DATA: 'COSTING_ADD_PRODUCT_COSTING_DATA',
  COSTING_LIST_PRODUCT_COSTING: 'COSTING_LIST_PRODUCT_COSTING',
  COSTING_SET_PRODUCT_COSTING_DETAIL_BY_ID: 'COSTING_SET_PRODUCT_COSTING_DETAIL_BY_ID',
  COSTING_REMOVE_PRODUCT_COSTING: 'COSTING_REMOVE_PRODUCT_COSTING',
  COSTING_PRODUCT_COSTING_UNMOUNT: 'COSTING_PRODUCT_COSTING_UNMOUNT',
  COSTING_DUPLICATE_PRODUCT_COSTING_DETAIL: 'COSTING_DUPLICATE_PRODUCT_COSTING_DETAIL',
  COSTING_SELECT_ALL_PRODUCT: 'COSTING_SELECT_ALL_PRODUCT',
  COSTING_LIST_PRODUCT_COSTING_COMMENT: 'COSTING_LIST_PRODUCT_COSTING_COMMENT',
  COSTING_SET_DATA_TO_UPDATE_COMMENT: 'COSTING_SET_DATA_TO_UPDATE_COMMENT',
  COSTING_UPDATE_COSTING_COMMENT: 'COSTING_UPDATE_COSTING_COMMENT',
  COSTING_SET_PRODUCT_COLOUR: 'COSTING_SET_PRODUCT_COLOUR',
  COSTING_SET_PRODUCT_LENGTHS: 'COSTING_SET_PRODUCT_LENGTHS',
  COSTING_SET_PRODUCT_SIZES: 'COSTING_SET_PRODUCT_SIZES',
  COSTING_SET_FILTER_SORT_DATA: 'COSTING_SET_FILTER_SORT_DATA',
  COSTING_SET_UNINTEGRATED_COLOURWAYS: 'COSTING_SET_UNINTEGRATED_COLOURWAYS',
  COSTING_SET_UNINTEGRATED_UNITS: 'COSTING_SET_UNINTEGRATED_UNITS',
  COSTING_SET_UNINTEGRATED_PRODUCT_COSTING_DETAILS: 'COSTING_SET_UNINTEGRATED_PRODUCT_COSTING_DETAILS',
  COSTING_SET_CLEARED_HIGHLIGHTS_STATUS: 'COSTING_SET_CLEARED_HIGHLIGHTS_STATUS',
  COSTING_SET_REFRESHED_STATE: 'COSTING_SET_REFRESHED_STATE',
  COSTING_SET_PRODUCT_COSTING_EXCHANGE_RATE: 'COSTING_SET_PRODUCT_COSTING_EXCHANGE_RATE',
  COSTING_SET_PRODUCT_COSTING_TEMPLATE_DETAIL_BY_ID: 'COSTING_SET_PRODUCT_COSTING_TEMPLATE_DETAIL_BY_ID',
  COSTING_SET_UNINTEGRATED_COSTINGSTAGE: 'COSTING_SET_UNINTEGRATED_COSTINGSTAGE',
  COSTING_SET_PRODUCT_SUPPLERS_FACTORIES: 'COSTING_SET_PRODUCT_SUPPLERS_FACTORIES',
  COSTING_SET_UNMOUNT_STATE: 'COSTING_SET_UNMOUNT_STATE',
  COSTING_SET_COSTING_STATS: 'COSTING_SET_COSTING_STATS',
} as const;

export type ProductCostingActionTypeValue = (typeof ProductCostingType)[keyof typeof ProductCostingType];

export default ProductCostingType;

/* ------------------------------------------------------------------ domain shapes */

/**
 * A BOM component row as returned by `getBomForProductTesting`. The reducer re-sequences these
 * by `bomType.name` against a fixed five-name order, so that field is the only one it reads.
 */
export interface BomComponent {
  _id?: string;
  bomType?: NamedRef;
  [key: string]: unknown;
}

/** A colourway as returned by `fetchProductColourwaysService`. */
export interface ProductColour {
  _id?: string;
  name?: string;
  newReferences?: unknown;
  [key: string]: unknown;
}

/** A unit-of-measure entry, used for both the integrated and un-integrated supplier flows. */
export interface ProductUnit {
  _id?: string;
  name?: string;
  label?: string;
  value?: string;
  [key: string]: unknown;
}

/**
 * One costing document in the list page. The detail page reads a far wider set of fields, so the
 * index signature carries the rest rather than enumerating a schema that the API owns.
 */
export interface CostingRecord {
  _id?: string;
  status?: string;
  isDefault?: boolean;
  isArchived?: boolean;
  [key: string]: unknown;
}

/**
 * The full costing document loaded by `getProductCostingDetailsById`. Only the branches the
 * context provider destructures are named; everything else stays under the index signature.
 */
export interface ProductCostingDetail {
  _id?: string;
  status?: string;
  markAsComplete?: boolean;
  isAverageCosting?: boolean;
  isDetailedCosting?: boolean;
  colourWayToggle?: boolean;
  sizeLengthToggle?: boolean;
  sizes?: string[];
  productInfo?: Record<string, unknown>;
  averageCosting?: Record<string, unknown>;
  quantities?: Record<string, unknown>;
  detailedCosting?: Record<string, unknown>;
  additional?: Record<string, unknown>;
  additionalComponent?: Record<string, unknown>;
  specificationRequirement?: Record<string, unknown>;
  testing?: Record<string, unknown>;
  productTemplate?: CostingTemplateDetail;
  landedInformation?: { landedInfoArr?: unknown[] };
  landedInformations?: StoredLandedInformationEntry[];
  [key: string]: unknown;
}

/** One persisted per-distribution-centre landed block on the costing document. */
export interface StoredLandedInformationEntry {
  distributionCentreName?: string;
  distributionCentreCurrency?: string;
  landedInfoArr?: unknown[];
  landedExchangeRateInformation?: Record<string, unknown>;
}

/** One configurable field on the costing template's "costing info" panel. */
export interface CostingInfoField {
  _id: string;
  type?: string;
  value?: unknown;
  isDefault?: boolean;
  [key: string]: unknown;
}

/** A costing template as returned by `getCostingTemplateDetailColSizeLenByIdsService`. */
export interface CostingTemplateDetail {
  _id?: string;
  name?: string;
  costingInfo?: CostingInfoField[];
  landedInfo?: unknown[];
  /* Named rather than left to the index signature, so the Spec Requirements card reads them without
     the `as boolean | undefined` cast every other section flag needs. */
  specificationRequirementTitle?: string;
  isSpecificationRequirementActive?: boolean;
  specificationRequirementInfo?: Array<{ title?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

/** A comment on a costing document. */
export interface CostingComment {
  _id?: string;
  comment?: string;
  [key: string]: unknown;
}

/** The aggregate figures rendered as header stat chips above the list grid. */
export type CostingStats = Record<string, unknown>;

/** Season/year pair used to look up the exchange-rate table. */
export interface ExchangeRateLookupParams {
  seasonId?: unknown;
  year?: unknown;
}

/** Filter + sort + paging state for the list page. */
export interface ProductCostingFilterValues {
  supplier: unknown[];
  colourway: unknown[];
  currency: unknown[];
  incoterms: unknown[];
  searchText: string;
  pageIndex: number;
  pageSize: number;
  sortOrder: string;
  sortPropertyName: string;
  loading: boolean;
  sampleRef: unknown[];
}

/** Query parameters accepted by `getProductCostingListService`. */
export interface ProductCostingListParams {
  pageIndex?: number | undefined;
  pageSize?: number | undefined;
  /* `| undefined` spelled out because callers build these params from optional route params and
     nullable state; `exactOptionalPropertyTypes` rejects the implicit form. */
  devOrderId?: string | undefined;
  costingTemplateId?: string | null | undefined;
  searchText?: string | undefined;
  isScrolled?: boolean | undefined;
  [key: string]: unknown;
}

/* ------------------------------------------------------------------ slice state */

export interface ProductCostingSliceState {
  bomCompoList: BomComponent[];
  productCosting: CostingRecord[];
  productCostingDetail: ProductCostingDetail | null;
  productCostingPreviousData: ProductCostingDetail | null;
  completionData: unknown;
  productCostingTotalCount: number;
  allSelectedIds: string[];
  costingCommentList: CostingComment[];
  editCommentData: CostingComment | null;
  productColours: ProductColour[];
  productLengths: string[];
  productSizes: string[];
  filterValues: ProductCostingFilterValues;
  productUnitList: ProductUnit[];
  clearedHighlights: boolean;
  isRefreshed: boolean;
  productExchnageRate: unknown;
  costingTemplateDetail: CostingTemplateDetail | null;
  costingStageListForNonIntegratedSupp: NamedRef[];
  supplierFactories: unknown[];
  /* Undefined is reachable: COSTING_LIST_PRODUCT_COSTING writes the payload's `averageTotalCal`
     verbatim, and the list response omits it when there are no expanded stats. Kept rather than
     coerced to null so the value the consumers see is byte-identical to the JavaScript original. */
  averageTotalCal: CostingStats | null | undefined;
}

/* ------------------------------------------------------------------ action union */

export interface SetTestingBomListAction {
  type: typeof ProductCostingType.COSTING_GET_TESTING_BOM_LIST;
  payload: BomComponent[];
}

export interface ListProductCostingPayload {
  data: CostingRecord[];
  totalCount: number;
  searchText?: string | undefined;
  isScrolled?: boolean | undefined;
  averageTotalCal?: CostingStats | undefined;
}

export interface ListProductCostingAction {
  type: typeof ProductCostingType.COSTING_LIST_PRODUCT_COSTING;
  payload: ListProductCostingPayload;
}

export interface MarkAsCompleteAction {
  type: typeof ProductCostingType.COSTING_MAEK_AS_COMPLETE;
  payload: unknown;
}

export interface SetProductCostingDetailAction {
  type: typeof ProductCostingType.COSTING_SET_PRODUCT_COSTING_DETAIL_BY_ID | typeof ProductCostingType.COSTING_SET_UNINTEGRATED_PRODUCT_COSTING_DETAILS;
  payload: ProductCostingDetail;
}

export interface RemoveProductCostingAction {
  type: typeof ProductCostingType.COSTING_REMOVE_PRODUCT_COSTING;
  payload: string[];
}

export interface DuplicateProductCostingDetailAction {
  type: typeof ProductCostingType.COSTING_DUPLICATE_PRODUCT_COSTING_DETAIL;
  payload: ProductCostingDetail;
}

export interface SelectAllProductAction {
  type: typeof ProductCostingType.COSTING_SELECT_ALL_PRODUCT;
  payload: string[];
}

export interface ListCostingCommentAction {
  type: typeof ProductCostingType.COSTING_LIST_PRODUCT_COSTING_COMMENT;
  payload: CostingComment[];
}

export interface SetEditCommentDataAction {
  type: typeof ProductCostingType.COSTING_SET_DATA_TO_UPDATE_COMMENT;
  payload: CostingComment;
}

export interface SetProductColourAction {
  type: typeof ProductCostingType.COSTING_SET_PRODUCT_COLOUR | typeof ProductCostingType.COSTING_SET_UNINTEGRATED_COLOURWAYS;
  payload: ProductColour[];
}

export interface SetProductLengthsAction {
  type: typeof ProductCostingType.COSTING_SET_PRODUCT_LENGTHS;
  payload: string[];
}

export interface SetProductSizesAction {
  type: typeof ProductCostingType.COSTING_SET_PRODUCT_SIZES;
  payload: string[];
}

export interface SetFilterSortDataAction {
  type: typeof ProductCostingType.COSTING_SET_FILTER_SORT_DATA;
  payload: Partial<ProductCostingFilterValues>;
}

export interface SetUnintegratedUnitsAction {
  type: typeof ProductCostingType.COSTING_SET_UNINTEGRATED_UNITS;
  payload: ProductUnit[];
}

export interface SetClearedHighlightsAction {
  type: typeof ProductCostingType.COSTING_SET_CLEARED_HIGHLIGHTS_STATUS;
  payload: boolean;
}

export interface SetRefreshedStateAction {
  type: typeof ProductCostingType.COSTING_SET_REFRESHED_STATE;
  payload: boolean;
}

export interface SetExchangeRateAction {
  type: typeof ProductCostingType.COSTING_SET_PRODUCT_COSTING_EXCHANGE_RATE;
  payload: unknown;
}

export interface UnmountAction {
  type: typeof ProductCostingType.COSTING_PRODUCT_COSTING_UNMOUNT;
}

export interface SetCostingTemplateDetailAction {
  type: typeof ProductCostingType.COSTING_SET_PRODUCT_COSTING_TEMPLATE_DETAIL_BY_ID;
  payload: CostingTemplateDetail;
}

export interface SetUnintegratedCostingStageAction {
  type: typeof ProductCostingType.COSTING_SET_UNINTEGRATED_COSTINGSTAGE;
  payload: NamedRef[];
}

export interface SetSupplierFactoriesAction {
  type: typeof ProductCostingType.COSTING_SET_PRODUCT_SUPPLERS_FACTORIES;
  payload: unknown[];
}

export interface SetUnmountStateAction {
  type: typeof ProductCostingType.COSTING_SET_UNMOUNT_STATE;
}

export interface SetCostingStatsAction {
  type: typeof ProductCostingType.COSTING_SET_COSTING_STATS;
  payload: CostingStats;
}

/* Declared in the type map and dispatched nowhere in this slice, but kept on the union so the
   constant stays referenced and any future dispatch site type-checks. Matches the original,
   which also declared them without a reducer case. */
export interface AddProductCostingDataAction {
  type: typeof ProductCostingType.COSTING_ADD_PRODUCT_COSTING_DATA;
  payload?: unknown;
}

export interface UpdateCostingCommentAction {
  type: typeof ProductCostingType.COSTING_UPDATE_COSTING_COMMENT;
  payload?: unknown;
}

export type ProductCostingAction =
  | SetTestingBomListAction
  | ListProductCostingAction
  | MarkAsCompleteAction
  | SetProductCostingDetailAction
  | RemoveProductCostingAction
  | DuplicateProductCostingDetailAction
  | SelectAllProductAction
  | ListCostingCommentAction
  | SetEditCommentDataAction
  | SetProductColourAction
  | SetProductLengthsAction
  | SetProductSizesAction
  | SetFilterSortDataAction
  | SetUnintegratedUnitsAction
  | SetClearedHighlightsAction
  | SetRefreshedStateAction
  | SetExchangeRateAction
  | UnmountAction
  | SetCostingTemplateDetailAction
  | SetUnintegratedCostingStageAction
  | SetSupplierFactoriesAction
  | SetUnmountStateAction
  | SetCostingStatsAction
  | AddProductCostingDataAction
  | UpdateCostingCommentAction;

/* State is `unknown` rather than a concrete root state because no thunk in this slice reads
   getState, and a concrete state type makes the dispatch generic contravariantly incompatible
   with the untyped .js thunks these components also dispatch. Same reasoning as CostingTsx. */
export type ProductCostingThunkAction<R = void> = ThunkAction<R, unknown, unknown, ProductCostingAction>;
export type ProductCostingDispatch = ThunkDispatch<unknown, unknown, ProductCostingAction>;
